import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Loader2, Info, Send, ShieldCheck, ArrowRight, Lock,
  CheckCircle2, Circle, MessageSquare,
} from 'lucide-react';
import QuickOTPModal from './QuickOTPModal';

const INTAKE_OPTIONS = ['Feb 2026', 'Jul 2026', 'Feb 2027', 'Jul 2027', 'Flexible / Next available'];
const REQUIRED_PCT = 80;

// Navy-blue brand palette for the chip + modal (matches the chip on cards)
const NAVY = '#0C447C';
const NAVY_SOFT = '#E6F1FB';
const NAVY_BORDER = '#85B7EB';

/**
 * Contact Institution enquiry modal — clone of ScholarshipApplyModal.
 *
 * Triggers:
 *   • "Contact for fees" chip on a course card → pass `course` + `institution`
 *   • "Contact Institution" button on profile   → pass only `institution`
 *
 * Phases (identical pattern):
 *   AUTH         → inline QuickOTPModal
 *   PROFILE_GATE → frozen submit + section checklist + ≥80% completion gate
 *   READY        → 3 fields only (intake, message, consent); rest auto-filled
 */
export default function ContactInstitutionModal({ course, institution, onClose, onSuccess }) {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState(user ? 'gate' : 'auth');
  const [profile, setProfile] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    intake: 'Feb 2026',
    message: '',
    consent: false,
  });

  const uniName = institution?.name || course?.university_name || 'this institution';
  const universityId = institution?.university_id || course?.university_id;
  const courseName = course?.name || null;

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    setLoadingProfile(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/profile/me`),
        axios.get(`${API}/profile/completion`),
      ]);
      setProfile(p.data);
      setCompletion(c.data);
      const pct = c.data?.completion || 0;
      setPhase(pct >= REQUIRED_PCT ? 'ready' : 'gate');
    } catch (e) {
      console.error('profile fetch failed', e);
      setPhase('gate');
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && phase !== 'auth') fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const onFocus = () => { if (user && phase === 'gate') fetchProfile(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, phase, fetchProfile]);

  const handleAuthSuccess = (u, _token) => {
    if (u && setUser) setUser(u);
    setPhase('gate');
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.message.trim() || form.message.trim().length < 15) {
      toast.error('Please share a bit more about your enquiry (min 15 characters)'); return;
    }
    if (!form.consent) {
      toast.error('Please accept the terms to continue'); return;
    }
    if (!universityId) {
      toast.error('Missing institution reference. Please refresh and try again.'); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: profile?.full_name || user?.name || '',
        email: profile?.email || user?.email || '',
        phone: profile?.phone || user?.phone || '',
        country: profile?.nationality || user?.country || '',
        intake: form.intake,
        message: form.message.trim(),
        consent: form.consent,
        course_id: course?.course_id || course?.id || null,
      };
      const res = await axios.post(`${API}/institutions/${universityId}/contact-enquiry`, payload);
      toast.success(res.data?.message || 'Enquiry sent ✦', { duration: 5000 });
      if (onSuccess) onSuccess(res.data);
      onClose?.();
      setTimeout(() => navigate('/dashboard'), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = completion?.steps || {};
  const SECTION_META = [
    { key: 'personal', label: 'Personal Information', why: 'Required for any application' },
    { key: 'education', label: 'Education History', why: 'Helps the institution evaluate fit' },
    { key: 'experience', label: 'Work Experience', why: 'Useful for postgrad enquiries' },
    { key: 'english', label: 'English Test (IELTS / PTE / TOEFL)', why: 'Critical for international admissions' },
    { key: 'documents', label: 'Documents (Passport, Transcripts)', why: 'Strengthens your enquiry' },
  ];
  const pct = completion?.completion || 0;
  const canSubmit = pct >= REQUIRED_PCT;

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="contact-institution-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* ─── Header ─── */}
        <div
          className="relative px-5 pt-5 pb-4 text-white shrink-0"
          style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #073461 100%)` }}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            data-testid="contact-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-1.5">
            <MessageSquare className="h-4 w-4 text-sky-200" />
            <span className="text-[10px] font-black uppercase tracking-widest text-sky-200">
              {courseName ? 'Contact for fees' : 'Contact Institution'}
            </span>
          </div>
          <h2 className="text-lg font-black">
            {courseName ? `Ask ${uniName} about this course` : `Send ${uniName} an enquiry`}
          </h2>
          <p className="text-xs text-white/70 mt-0.5">
            {courseName ? <><span className="text-white">{courseName}</span> · {uniName}</> : <>Direct line to admissions · walled-garden inside Unexa</>}
          </p>
        </div>

        {/* ─── Phase 1: AUTH ─── */}
        {phase === 'auth' && (
          <div className="flex-1 overflow-y-auto" data-testid="contact-phase-auth">
            <div className="px-5 py-3 flex items-start gap-2" style={{ background: NAVY_SOFT, borderBottom: `1px solid ${NAVY_BORDER}` }}>
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: NAVY }} />
              <p className="text-[11px] leading-relaxed" style={{ color: NAVY }}>
                Sign in to enquire. We'll auto-fill your details from your Unexa profile so you don't have to retype them.
              </p>
            </div>
            <div className="bg-white">
              <div className="otp-embedded">
                <QuickOTPModal
                  onSuccess={handleAuthSuccess}
                  onClose={() => {}}
                  dismissable={false}
                />
              </div>
            </div>
            <style>{`
              [data-testid="contact-phase-auth"] .otp-embedded > div {
                position: relative !important;
                background: transparent !important;
                backdrop-filter: none !important;
                padding: 0 !important;
                inset: auto !important;
              }
              [data-testid="contact-phase-auth"] .otp-embedded > div > div {
                box-shadow: none !important;
                max-width: 100% !important;
                width: 100% !important;
                border-radius: 0 !important;
              }
            `}</style>
          </div>
        )}

        {/* ─── Phase 2: GATE ─── */}
        {phase === 'gate' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="contact-phase-gate">
            {loadingProfile ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: NAVY }} />
              </div>
            ) : (
              <>
                <div
                  className="rounded-2xl p-4 flex items-center gap-4"
                  style={{ background: NAVY_SOFT, border: `1px solid ${NAVY_BORDER}` }}
                >
                  <div className="relative w-16 h-16 shrink-0">
                    <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="26" fill="none" stroke={NAVY_BORDER} strokeWidth="6" />
                      <circle
                        cx="32" cy="32" r="26" fill="none"
                        stroke={NAVY} strokeWidth="6"
                        strokeDasharray={`${(pct / 100) * 163} 163`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[13px] font-black" style={{ color: NAVY }}>{pct}%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-black text-slate-900">
                      Complete your profile to send enquiries
                    </h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed mt-0.5">
                      {uniName} reviews your full profile to give meaningful answers. You're {Math.max(REQUIRED_PCT - pct, 0)}% away.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {SECTION_META.map(s => {
                    const done = !!steps[s.key];
                    return (
                      <div
                        key={s.key}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border ${done ? 'bg-emerald-50/50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}
                        data-testid={`contact-gate-${s.key}`}
                      >
                        {done ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`text-[13px] font-bold ${done ? 'text-emerald-900' : 'text-slate-700'}`}>
                            {s.label}
                          </div>
                          <div className="text-[10px] text-slate-500">{s.why}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  disabled
                  className="w-full py-3.5 rounded-2xl bg-slate-100 text-slate-400 text-sm font-black cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="contact-submit-locked"
                >
                  <Lock className="h-4 w-4" />
                  Send Enquiry (locked)
                </button>

                <button
                  onClick={() => {
                    onClose?.();
                    navigate('/dashboard?tab=mydetails');
                  }}
                  className="w-full py-3 rounded-2xl text-white text-sm font-black shadow-lg flex items-center justify-center gap-2 transition-all"
                  style={{ background: NAVY, boxShadow: `0 10px 24px -8px ${NAVY}33` }}
                  data-testid="contact-complete-profile-cta"
                >
                  Complete My Profile
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-center text-[10px] text-slate-400">
                  Takes ~3 minutes. Unlocks enquiries to {uniName} and 500+ other institutions.
                </p>
              </>
            )}
          </div>
        )}

        {/* ─── Phase 3: READY ─── */}
        {phase === 'ready' && (
          <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="contact-phase-ready">
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-3.5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="text-[11px] font-black text-emerald-900 uppercase tracking-wider">Using your profile · {pct}% complete</span>
              </div>
              <div className="text-[12px] text-slate-700 leading-relaxed">
                <div><b>{profile?.full_name || user?.name}</b> · {profile?.nationality || user?.country || '—'}</div>
                {profile?.education?.highest_qualification && (
                  <div className="text-slate-600 mt-0.5">
                    {profile.education.highest_qualification} in {profile.education.field_of_study || '—'}
                    {profile.education.institution ? ` · ${profile.education.institution}` : ''}
                  </div>
                )}
                {profile?.english?.test_type && (
                  <div className="text-slate-600 mt-0.5">
                    {profile.english.test_type}
                    {profile.english.score ? ` · Score ${profile.english.score}` : ''}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { onClose?.(); navigate('/dashboard?tab=mydetails'); }}
                className="text-[11px] font-bold mt-2 underline"
                style={{ color: NAVY }}
                data-testid="contact-edit-profile-link"
              >
                Edit profile →
              </button>
            </div>

            {courseName && (
              <div
                className="rounded-xl px-3 py-2.5 text-[12px] font-semibold"
                style={{ background: NAVY_SOFT, border: `1px solid ${NAVY_BORDER}`, color: NAVY }}
                data-testid="contact-course-context"
              >
                <span className="opacity-70 uppercase tracking-wider text-[10px] mr-2">Course of interest</span>
                {courseName}
              </div>
            )}

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">📅 Intended intake</label>
              <div className="flex flex-wrap gap-1.5">
                {INTAKE_OPTIONS.map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => update('intake', opt)}
                    className="px-3 py-1.5 rounded-full text-xs font-bold border transition-all"
                    style={form.intake === opt
                      ? { background: NAVY, color: '#fff', borderColor: NAVY, boxShadow: '0 1px 0 rgba(12,68,124,0.18)' }
                      : { background: '#fff', color: '#475569', borderColor: '#e2e8f0' }}
                    data-testid={`contact-intake-chip-${opt.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">
                💬 Your message <span className="text-slate-400 lowercase normal-case">({form.message.length}/500)</span>
              </label>
              <textarea
                value={form.message}
                onChange={e => update('message', e.target.value.slice(0, 500))}
                rows={4}
                placeholder={courseName
                  ? `e.g. Could you share the international tuition fee per year for ${courseName}, English requirements, and intake dates?`
                  : `e.g. I'd like to know more about your scholarship options, English requirements, and intake dates.`}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': `${NAVY}1A` }}
                onFocus={e => (e.target.style.borderColor = NAVY)}
                onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                maxLength={500}
                data-testid="contact-message-input"
              />
            </div>

            <div className="rounded-xl p-3" style={{ background: NAVY_SOFT, border: `1px solid ${NAVY_BORDER}` }}>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: NAVY }} />
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Your enquiry goes straight into {uniName}'s Unexa inbox. They typically respond within 5-7 days with fees, intake details, and next steps.
                </p>
              </div>
              <label className="mt-2.5 flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={e => update('consent', e.target.checked)}
                  className="mt-0.5 w-4 h-4"
                  style={{ accentColor: NAVY }}
                  data-testid="contact-consent-checkbox"
                />
                <span>I agree to share my Unexa profile with {uniName} and accept Unexa's <a href="/terms" target="_blank" rel="noreferrer" className="underline" style={{ color: NAVY }}>Terms</a> &amp; <a href="/privacy" target="_blank" rel="noreferrer" className="underline" style={{ color: NAVY }}>Privacy</a>.</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !canSubmit}
              className="w-full py-3.5 rounded-2xl text-white text-sm font-black shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: NAVY, boxShadow: `0 14px 32px -10px ${NAVY}40` }}
              data-testid="contact-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {submitting ? 'Sending…' : 'Send Enquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
